import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  DataRow,
  LoadingGroup,
  LoadingSkeleton,
  Message,
  Messages,
  Table,
  Tag,
  useSimpleTableDataSource,
} from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";

import { useListMedicarePrescriptionPaymentPlanInvoicesQuery } from "@/App/api/medicarePrescriptionPaymentPlan";
import { UniformRowColorTable } from "@/App/components";
import { usePlanYear } from "@/App/pages";
import { MedicarePrescriptionPaymentPlanInvoice } from "@/App/types/medicarePrescriptionPaymentPlan";
import { formatDollars } from "@/App/utils/utils";

const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.invoiceHistoryPanel";

export const InvoiceHistoryPanel: FC = () => {
  const { t: translate } = useTranslation();
  const planYear = usePlanYear();

  const {
    data: invoices,
    isLoading: isLoadingInvoices,
    isError: isInvoicesError,
  } = useListMedicarePrescriptionPaymentPlanInvoicesQuery({ plan_year: planYear });

  const invoiceHistoryRows: DataRow<MedicarePrescriptionPaymentPlanInvoice>[] = useMemo(() => {
    return (invoices?.results || [])
      .filter(
        (payment) =>
          payment.reconciliation_status === "paid_in_full"
          || payment.reconciliation_status === "partially_paid"
          || payment.reconciliation_status === "unpaid",
      )
      .sort((a, b) => new Date(b.due_date).getTime() - new Date(a.due_date).getTime())
      .map((payment) => {
        return {
          id: payment.id,
          data: {
            ...payment,
          },
        };
      });
  }, [invoices]);

  const invoiceHistoryDataSource = useSimpleTableDataSource(invoiceHistoryRows);

  return (
    <Stack>
      <Heading>
        <Shelf>
          {translate(`${TRANSLATION_PATH}.invoiceHistory`)}
          <Tag type="info" style={{ display: "inline-flex", alignItems: "center" }}>
            {planYear}
          </Tag>
          <Shelf.Spacer />
        </Shelf>
      </Heading>

      <LoadingGroup loading={isLoadingInvoices}>
        <LoadingGroup.Placeholder
          fallback={
            <Stack spacing="mini">
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
              <Shelf spacing="mini">
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <LoadingSkeleton style={{ width: "125px", height: "32px" }} />
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          }
        >
          {!isInvoicesError && invoiceHistoryRows.length > 0 && (
            <Shelf>
              <UniformRowColorTable dataSource={invoiceHistoryDataSource}>
                <Table.Date
                  name="due_date"
                  initialWidth="span"
                  sortable
                  title={translate(`${TRANSLATION_PATH}.invoiceDate`)}
                  format="LL/dd/yyyy"
                />
                <Table.Custom
                  name="monthly_payment_amount"
                  initialWidth="span"
                  sortable
                  title={translate(`${TRANSLATION_PATH}.invoiceAmount`)}
                >
                  {(value) => formatDollars(value as number)}
                </Table.Custom>
                <Table.Custom
                  name="reconciliation_status"
                  initialWidth="span"
                  title={translate(`${TRANSLATION_PATH}.status`)}
                  align="center"
                >
                  {(reconciliation_status) => {
                    if (reconciliation_status === "paid_in_full") {
                      return <Tag type="success-high-contrast">{translate(`${TRANSLATION_PATH}.paid`)}</Tag>;
                    } else if (reconciliation_status === "partially_paid") {
                      return <Tag type="warning-high-contrast">{translate(`${TRANSLATION_PATH}.partiallyPaid`)}</Tag>;
                    } else if (reconciliation_status === "refunded") {
                      return <Tag type="info-high-contrast">{translate(`${TRANSLATION_PATH}.refunded`)}</Tag>;
                    } else if (reconciliation_status === "unpaid") {
                      return <Tag type="info-high-contrast">{translate(`${TRANSLATION_PATH}.unpaid`)}</Tag>;
                    }
                  }}
                </Table.Custom>
              </UniformRowColorTable>
              <Shelf.Spacer />
            </Shelf>
          )}

          <Message appearance={Messages.Informational} visible={!invoiceHistoryRows.length}>
            {translate(`${TRANSLATION_PATH}.noInvoices`)}
          </Message>

          <Message visible={isInvoicesError} appearance={Messages.Error}>
            {translate(`${TRANSLATION_PATH}.noInvoicesError`)}
          </Message>
        </LoadingGroup.Placeholder>
      </LoadingGroup>
    </Stack>
  );
};
